/**
 * /fiches/moto/plateau-parcours-a2 — Plan interactif du plateau moto A2 (Phase 31)
 *
 * Contient les 2 parcours officiels du plateau permis moto A2 :
 *   - PAL : Parcours à Allure Lente (temps min, précision, équilibre)
 *   - PAR : Parcours à Allure Rapide (évitement + freinage d'urgence)
 *
 * Chaque parcours est présenté :
 *   - Dans les 2 sens (Sens 1 = de gauche à droite / Sens 2 = miroir)
 *   - Avec les temps, distances et vitesses cibles
 *   - Barème A/B/C (favorable / défavorable / éliminatoire)
 *   - Notes pédagogiques pour mémoriser + pièges classiques
 *
 * Objectif : que l'élève puisse s'entraîner à visualiser et mémoriser les
 * enchaînements avant de monter sur la moto.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Printer, Clock, Target, AlertTriangle, ChevronRight, Zap, Gauge,
  BookOpen, RefreshCw, Info,
} from 'lucide-react';

const TABS = [
  { key: 'pal', label: 'Parcours Lent (PAL)', icon: Clock, color: 'emerald' },
  { key: 'par', label: 'Parcours Rapide (PAR)', icon: Zap, color: 'rose' },
];

export default function PlateauParcoursA2() {
  const [tab, setTab] = useState('pal');
  const [sens, setSens] = useState(1);

  useEffect(() => {
    document.title = 'Plateau A2 — Parcours interactifs · code2roues';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-24">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
          <Link to="/eleve/dashboard" className="text-white/70 hover:text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Retour
          </Link>
          <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500">Plateau A2 · Parcours</div>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-white/85 text-[12px] font-semibold transition print:hidden"
            data-testid="btn-print-plateau"
          >
            <Printer size={13} /> Imprimer
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Le plateau moto A2
          </h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Mémorise les 2 parcours officiels dans les 2 sens. Chaque schéma indique les temps, distances,
            barème et pièges à éviter. Visualise-les avant de rouler pour anticiper tes gestes.
          </p>
        </div>

        {/* Bloc prérequis officiels (7 exercices + déplacement moteur coupé) */}
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-4 mb-6" data-testid="prerequis-officiels">
          <div className="flex items-center gap-2 mb-2">
            <Info size={14} className="text-amber-400" />
            <h2 className="text-[11px] font-black uppercase tracking-widest text-amber-400">Les 7 exercices officiels de l&apos;épreuve</h2>
          </div>
          <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[12.5px] text-white/85 list-decimal pl-5 mb-3">
            <li><strong>Déplacement moteur coupé</strong> <span className="text-amber-300">(A/B uniquement, NON éliminatoire)</span></li>
            <li><strong>Allure lente SANS passager</strong> (chrono 16 s min)</li>
            <li><strong>Allure lente AVEC passager</strong> (montée après le freinage)</li>
            <li><strong>2 exercices à allure rapide</strong> (freinage + évitement)</li>
            <li><strong>Le freinage</strong> (50 km/h min, arrêt avant C5)</li>
            <li><strong>Slalom + évitement</strong> (40 km/h C7, 50 km/h C6 retour)</li>
            <li><strong>Demi-tours</strong> (dont un avec arrêt-redémarrage au point 4) + <strong>arrêt final</strong></li>
          </ol>
          <div className="pt-3 border-t border-amber-500/20 text-[11.5px] text-white/70 space-y-1">
            <div>📅 <strong>Réforme du 1er mars 2020</strong> : l&apos;interrogation orale + vérifications techniques sont désormais dans l&apos;ETM. Le plateau dure ~10 min par candidat.</div>
            <div>🎯 <strong>Notation A → C</strong> : chaque exercice noté séparément. <strong>C = éliminatoire</strong> sur tous les exercices SAUF le déplacement moteur coupé (A ou B seulement).</div>
            <div>🔁 <strong>2 essais autorisés</strong> en cas de faux départ. Le plateau reste valable 3 ans pour passer la circulation.</div>
          </div>
        </div>

        {/* Tabs parcours */}
        <div className="inline-flex gap-1 p-1 rounded-xl border border-white/10 bg-white/[0.03] mb-4" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                role="tab"
                aria-selected={active}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-lg text-[12.5px] font-semibold transition ${
                  active
                    ? (t.color === 'emerald' ? 'bg-emerald-600 text-white shadow' : 'bg-rose-600 text-white shadow')
                    : 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Toggle sens */}
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-white/40 font-semibold mr-1">Sens :</span>
          {[1, 2].map((s) => (
            <button
              key={s}
              onClick={() => setSens(s)}
              data-testid={`sens-${s}`}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold border transition ${
                sens === s
                  ? 'bg-white/10 border-white/25 text-white'
                  : 'bg-transparent border-white/10 text-white/50 hover:text-white hover:bg-white/[0.03]'
              }`}
            >
              <RefreshCw size={11} /> Sens {s} {s === 1 ? '(→)' : '(←)'}
            </button>
          ))}
        </div>

        {/* Contenu tab */}
        {tab === 'pal' ? <PALContent sens={sens} /> : <PARContent sens={sens} />}
      </main>

      <style>{`
        @page { size: A4 landscape; margin: 8mm; }
        @media print { html, body { background: #fff !important; color: #111 !important; } .print\\:hidden { display: none !important; } }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Parcours à Allure Lente (PAL)
// ═══════════════════════════════════════════════════════════════════════════
function PALContent({ sens }) {
  return (
    <>
      <SchemaCard title={`Parcours Lent · Sens ${sens}`} accent="emerald">
        <SVGParcoursLent sens={sens} />
      </SchemaCard>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <InfoTile icon={Clock} accent="emerald" label="Temps minimum" value="16 s" hint="Roue arrière de porte 1 à porte 2 · Ralenti moteur interdit" />
        <InfoTile icon={Target} accent="emerald" label="1ʳᵉ vitesse uniquement" value="Pas de 2ⁿᵈ" hint="Embrayage · accélérateur · frein arrière combinés" />
        <InfoTile icon={Gauge} accent="emerald" label="Point 4 (plot bleu)" value="Arrêt + redémarrage" hint="Zone neutralisée ±50 cm · guidon braqué" />
      </div>

      <StepList
        title="Enchaînement à mémoriser"
        color="emerald"
        steps={sens === 1 ? PAL_STEPS_SENS1 : PAL_STEPS_SENS2}
      />

      <Bareme
        color="emerald"
        rows={[
          { note: 'A', label: 'Favorable — ≥ 16 s', desc: 'Portion chronométrée en 16 s ou plus · aucune faute · pas d\'arrêt pied au sol' },
          { note: 'B', label: 'Défavorable — 14 à 16 s', desc: 'Ou 1 faute légère : >2 pieds à terre, plot déplacé, piquet touché' },
          { note: 'C', label: 'Éliminatoire — < 14 s', desc: 'Ou embrayage non maîtrisé, porte manquée, sortie de parcours, arrêt+pied au sol simultanés, calage au point 4' },
        ]}
      />

      <NotesBloc
        color="emerald"
        notes={[
          'Chrono : 16 s minimum entre porte 1 et porte 2 (déclencheur = roue arrière).',
          'RALENTI moteur INTERDIT : embrayage patiné + petit filet de gaz obligatoire.',
          'Regard TOUJOURS loin, sur la porte suivante, jamais sur la roue avant.',
          'Genoux serrés contre le réservoir → équilibre naturel.',
          'Zone neutralisée porte 1 : 1 seul essai de repositionnement toléré (repos moto autorisé).',
          'Point 4 : arrêt roue avant à l\'aplomb du plot bleu, redémarrage guidon braqué (attention calage = pas de repositionnement).',
        ]}
        pieges={[
          'Moins de 14 s = éliminatoire (C direct, l\'inspecteur t\'arrête au point 4 au 2ᵉ essai).',
          'Arrêt AVEC pied au sol simultané (hors zone neutralisée) = note C.',
          'Plus de 2-3 pieds à terre sur l\'ensemble = C éliminatoire.',
          'Calage au point 4 = pas de repositionnement possible, guidon braqué = chute.',
          'Regarder la roue avant → perte d\'équilibre → chute quasi-certaine.',
        ]}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Parcours à Allure Rapide (PAR)
// ═══════════════════════════════════════════════════════════════════════════
function PARContent({ sens }) {
  return (
    <>
      <SchemaCard title={`Parcours Rapide · Sens ${sens}`} accent="rose">
        <SVGParcoursRapide sens={sens} />
      </SchemaCard>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <InfoTile icon={Gauge} accent="rose" label="Freinage retour" value="≥ 50 km/h" hint="À la ligne C6 · 3ᵉ rapport min · Arrêt AVANT C5 (sec) / C4 (mouillé)" />
        <InfoTile icon={Zap} accent="rose" label="Slalom aller" value="≥ 40 km/h" hint="À la ligne C7 (3ᵉ plot) · marge 5 km/h A1 (soit 35 km/h)" />
        <InfoTile icon={Clock} accent="rose" label="Évitement retour" value="1 m × 7 m" hint="Couloir · zéro plot touché · contre-braquage net" />
      </div>

      <StepList
        title="Enchaînement à mémoriser"
        color="rose"
        steps={sens === 1 ? PAR_STEPS_SENS1 : PAR_STEPS_SENS2}
      />

      <Bareme
        color="rose"
        rows={[
          { note: 'A', label: 'Favorable', desc: 'Vitesse ≥ 50 km/h à C6 · 3ᵉ rapport engagé · arrêt avant C5 · évitement propre · zone d\'arrêt final respectée' },
          { note: 'B', label: 'Défavorable', desc: '3ᵉ rapport non engagé · guidonnage · perte adhérence roue arrière · légère anticipation freinage' },
          { note: 'C', label: 'Éliminatoire', desc: 'Vitesse < 50 km/h C6 (< 45 A1/A2) · < 40 C7 · plot renversé · freinage avant C6 · roue avant hors zone · chute' },
        ]}
      />

      <NotesBloc
        color="rose"
        notes={[
          '3ᵉ rapport ENGAGÉ au plus tard avant le début du freinage (sinon note B automatique).',
          'Radar mesure à la ligne C6 : ne relâche PAS l\'accélérateur avant d\'avoir franchi la ligne.',
          'Doigts NON posés sur le levier avant C6 (évite ambiguïté anticipation freinage).',
          'Freinage combiné 70/30 avant/arrière · les deux roues doivent rester au sol.',
          'Zone d\'arrêt final : arrêt total, ne bouge pas, ne dis rien, attends l\'inspecteur.',
        ]}
        pieges={[
          'Vitesse < 50 km/h C6 (ou 45 avec marge A1/A2) = éliminatoire.',
          'Vitesse < 40 km/h à C7 (ou 35 avec marge A1) = éliminatoire.',
          'Freinage déclenché AVANT la ligne C6 = C pour non-respect de l\'exercice.',
          'Un seul plot renversé dans couloir/mur d\'évitement = éliminatoire.',
          'Chute = échec IMMÉDIAT et interruption de l\'examen.',
        ]}
      />
    </>
  );
}

// ─── Étapes des parcours ────────────────────────────────────────────────
// PAL SENS 1 — parcours officiel lent (1ère vitesse uniquement, embrayage patiné)
const PAL_STEPS_SENS1 = [
  { t: 'Étape 1', txt: 'Départ arrêté (point 1). Zone neutralisée jusqu\'à la porte 1 : 1 seul essai de repositionnement autorisé.' },
  { t: 'Chrono', txt: 'Portion chronométrée entre porte 1 et porte 2 — TEMPS MINI 16 SECONDES. Roue arrière = déclencheur.' },
  { t: 'Après P2', txt: 'Sortie porte 2 SANS arrêt. Léger 10-15 km/h vers le couloir d\'évitement (rester en 1ʳᵉ).' },
  { t: 'Diagonale', txt: 'Traverser la piste en diagonale vers le plot bleu (point 4). Passer par l\'extérieur du 1er cône de slalom.' },
  { t: 'Point 4', txt: 'Arrêt roue avant à l\'aplomb du plot bleu. Zone neutralisée ±50 cm. Redémarrage guidon braqué (attention calage = pas de repositionnement).' },
  { t: 'Demi-tour', txt: 'Demi-tour serré à main droite sur largeur piste, sans franchir les lignes blanches (cercle ou ellipse).' },
  { t: 'Passager', txt: 'Après l\'épreuve de freinage, arrêt en zone C6 (C5 si mouillé) pour prendre le passager. Repartir sans caler.' },
  { t: 'Arrivée', txt: 'Zone d\'arrêt final (rectangle 4 plots bleus) : arrêt total, ne pas bouger, attendre l\'inspecteur.' },
];
const PAL_STEPS_SENS2 = PAL_STEPS_SENS1.map((s) => ({
  ...s,
  txt: `[Miroir] ${s.txt.replace('main droite', 'main gauche').replace('à gauche', 'à droite').replace('à droite', 'à gauche')}`,
}));
// PAR SENS 1 — parcours rapide (freinage + slalom + évitement)
const PAR_STEPS_SENS1 = [
  { t: 'Aller', txt: 'Départ extrémité piste. Monter 3 premiers rapports MIN avant ligne C6 (obligatoire, sinon note B).' },
  { t: 'Slalom aller', txt: 'Slalom entre plots. À la ligne C7 (3ᵉ plot) : vitesse MIN 40 km/h au radar (35 km/h pour A1).' },
  { t: 'Demi-tour', txt: 'Demi-tour en bout de piste sans renverser cône central ni franchir lignes blanches. 1 pied autorisé (comptabilisé).' },
  { t: 'Retour', txt: 'Ligne droite retour, 3ᵉ rapport min engagé. Vitesse MIN 50 km/h à la ligne C6 (marge 5 km/h A1).' },
  { t: 'Freinage', txt: 'Freinage combiné avant + arrière. Arrêt roue avant AVANT ligne C5 (sec) ou C4 (mouillé). Chute = échec immédiat.' },
  { t: 'Prise passager', txt: 'Passager monte : deux mains sur le pilote, genoux serrés, pieds sur cale-pieds. Zone neutralisée redémarrage.' },
  { t: 'Évitement', txt: 'Couloir évitement 1m latéral × 7m long. Aucun plot touché. Coup de guidon net + contre-braquage.' },
  { t: 'Arrêt final', txt: 'Zone d\'arrêt (4 plots bleus). Pneus entièrement dans la zone. Attendre l\'inspecteur en silence.' },
];
const PAR_STEPS_SENS2 = PAR_STEPS_SENS1.map((s) => ({
  ...s,
  txt: `[Miroir] ${s.txt}`,
}));

// ─── SVG Parcours Lent ──────────────────────────────────────────────────
function SVGParcoursLent({ sens }) {
  // Sens 2 : miroir horizontal (scale X = -1)
  const flip = sens === 2;
  return (
    <svg viewBox="0 0 800 260" className="w-full h-auto" role="img" aria-label={`Parcours lent sens ${sens}`}>
      <defs>
        <marker id="arr-e" markerWidth="10" markerHeight="10" refX="7" refY="5" orient="auto">
          <polygon points="0 0, 10 5, 0 10" fill="#10b981" />
        </marker>
      </defs>
      <g transform={flip ? 'translate(800,0) scale(-1,1)' : ''}>
        {/* Fond plateau */}
        <rect x="10" y="10" width="780" height="240" rx="8" fill="#111" stroke="#374151" strokeWidth="1.5" strokeDasharray="4 4" />
        {/* Départ */}
        <rect x="20" y="110" width="60" height="40" rx="4" fill="#065f46" opacity="0.4" stroke="#10b981" />
        <text x="50" y="135" textAnchor="middle" fill="#a7f3d0" fontSize="11" fontWeight="bold">DÉPART</text>

        {/* Slalom (5 plots) */}
        {[120, 170, 220, 270, 320].map((x, i) => (
          <g key={i}>
            <circle cx={x} cy={i % 2 === 0 ? 110 : 150} r="6" fill="#f97316" />
            <text x={x} y={i % 2 === 0 ? 95 : 175} textAnchor="middle" fill="#fdba74" fontSize="9">P{i + 1}</text>
          </g>
        ))}
        {/* Chemin slalom */}
        <path d="M 80 130 Q 145 100, 170 150 Q 195 100, 220 150 Q 245 100, 270 150 Q 295 100, 320 150 L 360 130"
          fill="none" stroke="#10b981" strokeWidth="2.5" strokeDasharray="6 3" markerEnd="url(#arr-e)" />

        {/* Figure en 8 (2 plots) */}
        <circle cx="420" cy="90" r="7" fill="#f97316" />
        <circle cx="420" cy="170" r="7" fill="#f97316" />
        <text x="420" y="80" textAnchor="middle" fill="#fdba74" fontSize="9">P6</text>
        <text x="420" y="190" textAnchor="middle" fill="#fdba74" fontSize="9">P7</text>
        <path d="M 380 130 C 380 60, 460 60, 460 130 C 460 200, 380 200, 380 130 Z" fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="6 3" />
        <text x="420" y="130" textAnchor="middle" fill="#a7f3d0" fontSize="10" fontStyle="italic">8</text>

        {/* Ligne droite lente (2 portes) — chronométrée 8s */}
        <line x1="510" y1="90" x2="510" y2="170" stroke="#fbbf24" strokeWidth="2" strokeDasharray="4 2" />
        <line x1="610" y1="90" x2="610" y2="170" stroke="#fbbf24" strokeWidth="2" strokeDasharray="4 2" />
        <text x="560" y="80" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="bold">⏱ 16 s min</text>
        <path d="M 470 130 L 660 130" stroke="#10b981" strokeWidth="2.5" strokeDasharray="6 3" markerEnd="url(#arr-e)" />

        {/* Demi-tour serré */}
        <circle cx="720" cy="130" r="42" fill="none" stroke="#10b981" strokeWidth="2" strokeDasharray="6 3" />
        <path d="M 720 88 A 42 42 0 0 1 720 172" fill="none" stroke="#10b981" strokeWidth="2.5" markerEnd="url(#arr-e)" />
        <text x="720" y="135" textAnchor="middle" fill="#a7f3d0" fontSize="9">U-turn</text>

        {/* Arrivée */}
        <rect x="670" y="200" width="60" height="30" rx="4" fill="#7f1d1d" opacity="0.4" stroke="#ef4444" />
        <text x="700" y="220" textAnchor="middle" fill="#fca5a5" fontSize="10" fontWeight="bold">ARRIVÉE</text>
        <path d="M 720 172 Q 700 200, 700 200" fill="none" stroke="#10b981" strokeWidth="2" markerEnd="url(#arr-e)" />
      </g>
    </svg>
  );
}

// ─── SVG Parcours Rapide ────────────────────────────────────────────────
function SVGParcoursRapide({ sens }) {
  const flip = sens === 2;
  return (
    <svg viewBox="0 0 800 220" className="w-full h-auto" role="img" aria-label={`Parcours rapide sens ${sens}`}>
      <defs>
        <marker id="arr-r" markerWidth="10" markerHeight="10" refX="7" refY="5" orient="auto">
          <polygon points="0 0, 10 5, 0 10" fill="#e11d48" />
        </marker>
      </defs>
      <g transform={flip ? 'translate(800,0) scale(-1,1)' : ''}>
        {/* Fond plateau */}
        <rect x="10" y="10" width="780" height="200" rx="8" fill="#111" stroke="#374151" strokeWidth="1.5" strokeDasharray="4 4" />

        {/* Départ */}
        <rect x="20" y="95" width="55" height="30" rx="4" fill="#7f1d1d" opacity="0.4" stroke="#e11d48" />
        <text x="48" y="115" textAnchor="middle" fill="#fca5a5" fontSize="10" fontWeight="bold">DÉPART</text>

        {/* Zone accélération */}
        <text x="180" y="80" textAnchor="middle" fill="#94a3b8" fontSize="10" fontStyle="italic">Accélération → 50 km/h</text>
        <path d="M 75 110 L 320 110" stroke="#e11d48" strokeWidth="3" markerEnd="url(#arr-r)" />

        {/* Radar (2 plots repères) */}
        <line x1="320" y1="70" x2="320" y2="150" stroke="#fbbf24" strokeWidth="2.5" />
        <line x1="360" y1="70" x2="360" y2="150" stroke="#fbbf24" strokeWidth="2.5" />
        <text x="340" y="60" textAnchor="middle" fill="#fbbf24" fontSize="10" fontWeight="bold">📡 RADAR 50 km/h</text>

        {/* Évitement */}
        <path d="M 360 110 Q 420 110, 450 60 Q 480 60, 510 110" fill="none" stroke="#e11d48" strokeWidth="3" markerEnd="url(#arr-r)" />
        <circle cx="470" cy="80" r="7" fill="#f97316" />
        <circle cx="470" cy="140" r="7" fill="#f97316" />
        <text x="470" y="30" textAnchor="middle" fill="#fdba74" fontSize="10">🚧 Évitement 1 m</text>
        <text x="470" y="200" textAnchor="middle" fill="#fdba74" fontSize="10">↑</text>

        {/* Zone de freinage (rouge hachurée) */}
        <rect x="580" y="90" width="120" height="40" fill="#7f1d1d" opacity="0.35" stroke="#e11d48" strokeWidth="2" />
        <pattern id="hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#e11d48" strokeWidth="1" opacity="0.4" />
        </pattern>
        <rect x="580" y="90" width="120" height="40" fill="url(#hatch)" />
        <text x="640" y="115" textAnchor="middle" fill="#fecaca" fontSize="10" fontWeight="bold">ZONE FREINAGE</text>

        {/* Path freinage */}
        <path d="M 510 110 L 640 110" stroke="#e11d48" strokeWidth="3" strokeDasharray="4 2" markerEnd="url(#arr-r)" />

        {/* Arrêt */}
        <rect x="720" y="95" width="55" height="30" rx="4" fill="#064e3b" opacity="0.4" stroke="#10b981" />
        <text x="748" y="115" textAnchor="middle" fill="#a7f3d0" fontSize="10" fontWeight="bold">ARRÊT</text>
        <path d="M 640 110 L 720 110" stroke="#94a3b8" strokeWidth="2" strokeDasharray="3 3" />
      </g>
    </svg>
  );
}

// ─── Sous-composants UI ─────────────────────────────────────────────────
function SchemaCard({ title, accent, children }) {
  const border = accent === 'emerald' ? 'border-emerald-500/30' : 'border-rose-500/30';
  const badge = accent === 'emerald' ? 'text-emerald-400' : 'text-rose-400';
  return (
    <div className={`rounded-2xl border ${border} bg-white/[0.02] p-4 sm:p-5 mb-4`} data-testid={`schema-${accent}`}>
      <div className={`text-[10px] font-black uppercase tracking-widest ${badge} mb-2`}>{title}</div>
      <div className="rounded-xl overflow-hidden bg-black/40 border border-white/5">
        {children}
      </div>
    </div>
  );
}

function InfoTile({ icon: Icon, accent, label, value, hint }) {
  const bg = accent === 'emerald' ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300' : 'bg-rose-500/10 border-rose-500/25 text-rose-300';
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
      <div className={`w-9 h-9 rounded-lg border ${bg} flex items-center justify-center mb-2`}>
        <Icon size={15} />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">{label}</div>
      <div className="text-xl font-black text-white mt-0.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</div>
      <div className="text-[11.5px] text-white/50 mt-0.5">{hint}</div>
    </div>
  );
}

function StepList({ title, color, steps }) {
  const accent = color === 'emerald' ? 'text-emerald-400 border-emerald-500/25 bg-emerald-500/[0.06]' : 'text-rose-400 border-rose-500/25 bg-rose-500/[0.06]';
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={14} className={color === 'emerald' ? 'text-emerald-400' : 'text-rose-400'} />
        <h3 className="text-[13px] font-black uppercase tracking-wider text-white">{title}</h3>
      </div>
      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 group">
            <span className={`shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md border text-[10.5px] font-bold ${accent}`}>
              <Clock size={10} /> {s.t}
            </span>
            <div className="flex-1 text-[13px] text-white/85 leading-snug pt-0.5">{s.txt}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Bareme({ color, rows }) {
  const badge = (n) => n === 'A' ? 'bg-emerald-500 text-white' : n === 'B' ? 'bg-amber-500 text-white' : 'bg-red-600 text-white';
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Target size={14} className={color === 'emerald' ? 'text-emerald-400' : 'text-rose-400'} />
        <h3 className="text-[13px] font-black uppercase tracking-wider text-white">Barème officiel</h3>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.note} className="flex items-start gap-3">
            <span className={`shrink-0 w-9 h-9 rounded-lg font-black text-lg inline-flex items-center justify-center ${badge(r.note)}`}>
              {r.note}
            </span>
            <div className="flex-1">
              <div className="text-white font-semibold text-[13px]">{r.label}</div>
              <div className="text-white/60 text-[12px] mt-0.5">{r.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotesBloc({ color, notes, pieges }) {
  return (
    <div className="grid md:grid-cols-2 gap-3 mb-4">
      <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Info size={14} className="text-emerald-400" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-emerald-400">À retenir</h3>
        </div>
        <ul className="space-y-1.5">
          {notes.map((n, i) => (
            <li key={i} className="flex items-start gap-2 text-[12.5px] text-white/85 leading-snug">
              <ChevronRight size={12} className="text-emerald-400 shrink-0 mt-0.5" />
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} className="text-amber-400" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-amber-400">Pièges classiques</h3>
        </div>
        <ul className="space-y-1.5">
          {pieges.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-[12.5px] text-white/85 leading-snug">
              <ChevronRight size={12} className="text-amber-400 shrink-0 mt-0.5" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
